import React from 'react';
import Lozenge from '@atlaskit/lozenge';
import { Section as SectionDI, SkeletonItem as SkeletonItemDI } from '@atlaskit/side-navigation';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import { useIntlV2 as useIntlDI } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { TitleHeader as TitleHeaderDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/title-header/main.tsx';
import { matchUrlPathname as matchUrlPathnameDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { ConnectMenu as ConnectMenuDI } from '@atlassian/jira-navigation-apps-sidebar-connect/src/common/ui/connect-menu/main.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useReportFeatures as useReportFeaturesDI } from '../../../controllers/features/main.tsx';
import { createReportUrl } from '../../utils/index.tsx';
import { JsdReports as JsdReportsDI } from './jsd-reports/main.tsx';
import messages from './messages.tsx';
import { ReportActions as ReportActionsDI } from './report-actions/main.tsx';
import type { ReportsProps } from './types.tsx';

/**
 * @deprecated This is a Nav3 component. If you do any changes here, please apply and QA them at Nav4 component as well: src/packages/navigation-apps/sidebar-nav4/jsm-project-menu
 */
export const Reports = ({
	ConnectMenu = ConnectMenuDI,
	JsdReports = JsdReportsDI,
	MenuItem = MenuItemDI,
	ReportActions = ReportActionsDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	TitleHeader = TitleHeaderDI,
	connectItems,
	matchUrlPathname = matchUrlPathnameDI,
	projectKey,
	// eslint-disable-next-line react-hooks/react-compiler
	useIntl = useIntlDI,
	// eslint-disable-next-line react-hooks/react-compiler
	useReportFeatures = useReportFeaturesDI,
}: ReportsProps) => {
	// eslint-disable-next-line react-hooks/react-compiler
	const { data: features } = useReportFeatures();
	// eslint-disable-next-line react-hooks/react-compiler
	const { formatMessage } = useIntl();
	const licensedProducts = useLicensedProducts();
	const shouldShowAtlassianAnalytics =
		licensedProducts && licensedProducts[LICENSED_PRODUCTS.ATLASSIAN_ANALYTICS];

	if (features == null || projectKey == null) return <SkeletonItem />;

	const BackMenuItem = {
		href: createReportUrl(projectKey, ''),
		title: formatMessage(messages.reports),
	};
	return (
		<>
			<TitleHeader>{formatMessage(messages.reports)}</TitleHeader>
			<Section title={formatMessage(messages.defaultReports)}>
				{features.hasWorkload && (
					<MenuItem
						analytics={{ itemId: 'workload' }}
						href={createReportUrl(projectKey, '/workload')}
						selectedOn={matchUrlPathname()}
					>
						{formatMessage(messages.workload)}
					</MenuItem>
				)}
				{features.hasSatisfaction && (
					<MenuItem
						analytics={{ itemId: 'all-rating' }}
						href={createReportUrl(projectKey, '/all-rating')}
						selectedOn={matchUrlPathname()}
					>
						{formatMessage(messages.satisfaction)}
					</MenuItem>
				)}
				{features.hasRequestsDeflected && (
					<MenuItem
						analytics={{ itemId: 'kb-requests-deflected' }}
						href={createReportUrl(projectKey, '/kb-requests-deflected')}
						selectedOn={matchUrlPathname()}
					>
						{formatMessage(messages.requestsDeflected)}
					</MenuItem>
				)}
				{features.hasRequestsResolved && (
					<MenuItem
						analytics={{ itemId: 'kb-requests-resolved' }}
						href={createReportUrl(projectKey, '/kb-requests-resolved')}
						selectedOn={matchUrlPathname()}
					>
						{formatMessage(messages.requestsResolved)}
					</MenuItem>
				)}

				{shouldShowAtlassianAnalytics && (
					<MenuItem
						analytics={{ itemId: 'atlassian-analytics' }}
						href={createReportUrl(projectKey, '/atlassian-analytics')}
						iconAfter={<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>}
						selectedOn={matchUrlPathname()}
					>
						{formatMessage(messages.itemAtlassianAnalytics)}
					</MenuItem>
				)}
			</Section>
			<JsdReports title={formatMessage(messages.custom)} />
			<ReportActions features={features} projectKey={projectKey} />
			<ConnectMenu items={connectItems} overrides={{ BackMenuItem }} />
		</>
	);
};
